import React from 'react';
import useAudioRecorder from '../../../../../Hooks/audio/useAudioRecorder';
import useAudioStream from '../../../../../Hooks/audio/useAudioStream';
import { useOrganisationData } from '../../../../../Hooks/useOrganisation';
import { AudioConfig } from '../../../../AudioConfigContext/Hooks/useAudioConfig';

const RECORDER_RESTART_DELAY_MS = 1000;
const MAX_RECORDER_START_RETRIES = 3;

export function useRestart(condition: boolean | null | undefined): [boolean | null | undefined, () => void] {
  const [enabled, setEnabled] = React.useState(condition);
  React.useEffect(() => {
    setEnabled(condition);
  }, [condition]);

  const enabledRef = React.useRef(enabled);
  React.useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    if (condition && !enabled && enabledRef.current) {
      timeout = setTimeout(() => {
        setEnabled(true);
      }, 100);
    }
    enabledRef.current = enabled;
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [enabled, condition]);
  return [enabled, () => { setEnabled(false); }];
}

function useRecorderStartRetry(
  restart: () => void,
  enabled: boolean | null | undefined,
  retryKey: string | null | undefined,
) {
  const retryCountRef = React.useRef(0);
  const retryKeyRef = React.useRef<string | null | undefined>(retryKey);
  const retryTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    if (retryKeyRef.current !== retryKey) {
      retryKeyRef.current = retryKey;
      retryCountRef.current = 0;
    }
  }, [retryKey]);

  React.useEffect(() => () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
  }, []);

  return React.useCallback((error: unknown) => {
    if (!enabled || !retryKey) return;

    if (retryCountRef.current >= MAX_RECORDER_START_RETRIES) {
      console.error('Audio recorder restart retry limit reached', error);
      return;
    }

    retryCountRef.current += 1;
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    retryTimeoutRef.current = setTimeout(() => {
      retryTimeoutRef.current = null;
      restart();
    }, RECORDER_RESTART_DELAY_MS);
  }, [enabled, restart, retryKey]);
}

function useAudioCaptureCore(
  stream: MediaStream | null,
  enabled: boolean | null | undefined,
  onRecorderError?: (error: unknown) => void,
): Uint8Array | null {
  const [lastAudioBlob, setLastAudioBlob] = React.useState<Uint8Array | null>(null);
  const enabledRef = React.useRef(enabled);

  React.useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  function callback(data: Uint8Array) {
    if (enabledRef.current) {
      setLastAudioBlob(data);
    } else {
      setLastAudioBlob(null);
    }
  }

  useAudioRecorder(enabled ? stream : null, (data) => { callback(new Uint8Array(data)); }, onRecorderError);

  React.useEffect(() => {
    if (!enabled) {
      setLastAudioBlob(null);
    }
  }, [enabled]);

  return lastAudioBlob;
}

export function useAudioCaptureFromStream(
  stream: MediaStream | null,
  inProgress: boolean,
  retryKey?: string | null,
  restartStream?: () => void,
): [Uint8Array | null, () => void] {
  const { enableCoreAsrApi: settingsEnabled } = useOrganisationData();
  const [enabled, restart] = useRestart(inProgress && settingsEnabled);
  const retryRestart = restartStream ?? restart;
  const recorderErrorRetry = useRecorderStartRetry(retryRestart, enabled, retryKey);
  return [useAudioCaptureCore(stream, enabled, recorderErrorRetry), retryRestart];
}

export default function useAudioCapture(
  inProgress: boolean,
  audioConfig: AudioConfig,
  retryKey?: string | null,
): [Uint8Array | null, () => void] {
  const { enableCoreAsrApi: settingsEnabled } = useOrganisationData();
  const [enabled, restart] = useRestart(inProgress && settingsEnabled);
  const stream = useAudioStream(!!enabled, audioConfig);
  const recorderErrorRetry = useRecorderStartRetry(restart, enabled, retryKey);
  return [useAudioCaptureCore(stream, enabled, recorderErrorRetry), restart];
}
